// 網址觸發登入／註冊彈窗的判定與正規化（純函式，無 Nuxt runtime 依賴）
// 規格：spec/JT-1784716920_url-modal-trigger-announcement-deferral.md

export const MODAL_INTENT = {
  REGISTER: 'register',
  LOGIN: 'login',
}

// 由網址觸發、會讓公告延後的彈窗
export const AUTH_PANEL_MODALS = ['ModalPanelLogin', 'ModalPanelSignUp']

// 正規化後（去語系前綴、去結尾斜線、轉小寫）的觸發路徑
const PATH_INTENT_MAP = {
  '/register': MODAL_INTENT.REGISTER,
  '/login': MODAL_INTENT.LOGIN,
  '/guest/register': MODAL_INTENT.REGISTER,
  '/guest/login': MODAL_INTENT.LOGIN,
}

const GUEST_SCOPE = '/guest'

// 根路徑必須保留 '/'，其餘去掉結尾斜線以統一比對
const stripTrailingSlash = (path) => {
  const stripped = String(path || '/').replace(/\/+$/, '')
  return stripped || '/'
}

// 本專案語系碼一律為 xx-XX 形式（@nuxtjs/i18n locales 設定）
const LOCALE_SEGMENT = /^[a-z]{2}-[A-Z]{2}$/

// 只切「完整的」語系區段，避免 /registerX、/th-THX 這類同字首路徑被誤切
// locales 未提供時（例：plugin 於 $i18n 就緒前呼叫）退回 xx-XX 形式判斷
export const stripLocalePrefix = (path, locales = []) => {
  const source = String(path || '/')
  for (const locale of locales) {
    if (source === `/${locale}`) return { locale, basePath: '/' }
    if (source.startsWith(`/${locale}/`)) return { locale, basePath: source.slice(locale.length + 1) }
  }

  if (!locales.length) {
    const [, first] = source.split('/')
    if (first && LOCALE_SEGMENT.test(first)) {
      const basePath = source.slice(first.length + 1)
      return { locale: first, basePath: basePath || '/' }
    }
  }

  return { locale: null, basePath: source }
}

export const normalizeBasePath = (path, locales = []) => {
  const { basePath } = stripLocalePrefix(path, locales)
  return stripTrailingSlash(basePath).toLowerCase()
}

// 需登入頁面（/main 及其子路由）不吃 modal 參數與觸發路徑（規格第 8 節）
export const isAuthRequiredPath = (basePath) => {
  const normalized = stripTrailingSlash(basePath).toLowerCase()
  return normalized === '/main' || normalized.startsWith('/main/')
}

// 陣列型 query（?error=a&error=b）非預期輸入，一律視為無錯誤碼
export const hasErrorParam = (query = {}) => typeof query?.error === 'string' && query.error.trim() !== ''

const parseModalQuery = (value) => {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  return normalized === MODAL_INTENT.REGISTER || normalized === MODAL_INTENT.LOGIN ? normalized : null
}

const NO_INTENT = { intent: null, source: null }

export const resolveModalIntent = ({ path, query = {}, locales = [] } = {}) => {
  // 錯誤參數優先：只顯示錯誤彈窗，忽略 modal 參數與路徑觸發
  if (hasErrorParam(query)) return { ...NO_INTENT }

  const basePath = normalizeBasePath(path, locales)
  if (isAuthRequiredPath(basePath)) return { ...NO_INTENT }

  // 路徑優先於參數
  const pathIntent = PATH_INTENT_MAP[basePath]
  if (pathIntent) return { intent: pathIntent, source: 'path' }

  const queryIntent = parseModalQuery(query?.modal)
  return queryIntent ? { intent: queryIntent, source: 'query' } : { ...NO_INTENT }
}

// 回傳需要導向的目標；已是正規形式（或無意圖）時回傳 null 代表不需導頁
export const buildNormalizedRoute = ({ path, query = {}, locales = [] } = {}) => {
  const { intent } = resolveModalIntent({ path, query, locales })
  if (!intent) return null

  const { locale } = stripLocalePrefix(path, locales)
  const basePath = normalizeBasePath(path, locales)
  const scope = basePath === GUEST_SCOPE || basePath.startsWith(`${GUEST_SCOPE}/`) ? GUEST_SCOPE : ''
  const targetPath = `${locale ? `/${locale}` : ''}${scope}/${intent}`

  const { modal: _modal, ...restQuery } = query || {}
  // 大小寫不符時 stripTrailingSlash(path) 與 targetPath 仍會不同，故仍會導頁修正
  if (stripTrailingSlash(path) === targetPath && !('modal' in (query || {}))) return null

  return { path: targetPath, query: restQuery }
}

// 站台未開放註冊時，register 意圖 fallback 開登入面板
export const resolveModalComponent = ({ intent, hasRegisterMethod } = {}) => {
  if (intent === MODAL_INTENT.LOGIN) return 'ModalPanelLogin'
  if (intent === MODAL_INTENT.REGISTER) return hasRegisterMethod ? 'ModalPanelSignUp' : 'ModalPanelLogin'
  return null
}

export const shouldOpenModalOnEntry = ({ intent, hasError, isLoggedIn } = {}) => Boolean(intent) && !hasError && !isLoggedIn
